// Pure session-tracking utilities backed by localStorage + sessionStorage.
// No React imports — safe to call from posthog.ts before the React tree mounts.

const KEYS = {
  LAST_VISITED: "hh:analytics:last_visited",
  PAGE_COUNTS: "hh:analytics:page_counts",
  PAGE_COUNTS_RESET_AT: "hh:analytics:page_counts_reset_at",
  VISIT_LOG: "hh:analytics:visit_log",             // localStorage — array of timestamps
  SESSION_START: "hh:analytics:session_start",      // sessionStorage — first tab load (any page)
  SESSION_RECORDED: "hh:analytics:session_recorded", // sessionStorage — dedup flag
  BOOKING_FORM_START: "hh:analytics:booking_form_start", // sessionStorage — when form mounted
} as const;

/**
 * Call once per browser session (tab open).
 * Stamps the session start time and appends a visit to the 30-day log.
 * Idempotent — safe to call multiple times; only acts on the first call per tab.
 */
export function initSession(): void {
  if (sessionStorage.getItem(KEYS.SESSION_RECORDED)) return;

  const now = Date.now();
  sessionStorage.setItem(KEYS.SESSION_RECORDED, "1");
  sessionStorage.setItem(KEYS.SESSION_START, String(now));

  try {
    const raw = localStorage.getItem(KEYS.VISIT_LOG);
    const log: number[] = raw ? (JSON.parse(raw) as number[]) : [];
    log.push(now);

    // Prune entries older than 30 days to avoid unbounded growth
    const cutoff = now - 30 * 24 * 60 * 60 * 1000;
    const pruned = log.filter((t) => t > cutoff);
    localStorage.setItem(KEYS.VISIT_LOG, JSON.stringify(pruned));
  } catch {
    // localStorage may be unavailable in private browsing — fail silently
  }

  // Reset page_counts every 90 days so removed/renamed routes don't skew getMostVisited()
  try {
    const resetAt = localStorage.getItem(KEYS.PAGE_COUNTS_RESET_AT);
    const cutoff90 = now - 90 * 24 * 60 * 60 * 1000;
    if (!resetAt || parseInt(resetAt, 10) < cutoff90) {
      localStorage.removeItem(KEYS.PAGE_COUNTS);
      localStorage.setItem(KEYS.PAGE_COUNTS_RESET_AT, String(now));
    }
  } catch {
    // fail silently
  }
}

/**
 * Record a page visit. Call AFTER firing the pageview event so that
 * `getLastVisited()` returns the *previous* page at event time.
 */
export function recordPageVisit(path: string): void {
  try {
    localStorage.setItem(KEYS.LAST_VISITED, path);

    const raw = localStorage.getItem(KEYS.PAGE_COUNTS);
    const counts: Record<string, number> = raw
      ? (JSON.parse(raw) as Record<string, number>)
      : {};
    counts[path] = (counts[path] ?? 0) + 1;
    localStorage.setItem(KEYS.PAGE_COUNTS, JSON.stringify(counts));
  } catch {
    // fail silently
  }
}

/** The page the user was on before the current one (or null on first visit). */
export function getLastVisited(): string | null {
  try {
    return localStorage.getItem(KEYS.LAST_VISITED);
  } catch {
    return null;
  }
}

/** The page path with the highest all-time visit count. */
export function getMostVisited(): string | null {
  try {
    const raw = localStorage.getItem(KEYS.PAGE_COUNTS);
    if (!raw) return null;

    const counts = JSON.parse(raw) as Record<string, number>;
    const entries = Object.entries(counts);
    if (entries.length === 0) return null;

    return entries.reduce((best, curr) => (curr[1] > best[1] ? curr : best))[0];
  } catch {
    return null;
  }
}

/**
 * How many browser sessions the user has started in the past 24 hours.
 * A value > 1 signals a high-intent "window shopper" returning to compare.
 */
export function getDailyFrequency(): number {
  try {
    const raw = localStorage.getItem(KEYS.VISIT_LOG);
    if (!raw) return 1;

    const log = JSON.parse(raw) as number[];
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return Math.max(log.filter((t) => t > cutoff).length, 1);
  } catch {
    return 1;
  }
}

/**
 * Stamp the moment the booking form mounted.
 * Call this from useBookingAnalytics on form_opened — it gives a clean
 * "time spent inside the form" denominator for the friction metric.
 */
export function stampBookingFormStart(): void {
  try {
    sessionStorage.setItem(KEYS.BOOKING_FORM_START, String(Date.now()));
  } catch {
    // fail silently
  }
}

/**
 * Elapsed seconds from when the booking form first opened to now.
 * This is the true form-friction metric — unaffected by time spent on
 * the landing page, login flow, or dashboard before entering the form.
 *
 * Falls back to session start (first tab open) if the form stamp is missing.
 */
export function getTimeToBookSeconds(): number {
  try {
    const formStart = sessionStorage.getItem(KEYS.BOOKING_FORM_START);
    const sessionStart = sessionStorage.getItem(KEYS.SESSION_START);
    const anchor = formStart ?? sessionStart;
    if (!anchor) return 0;
    return Math.round((Date.now() - parseInt(anchor, 10)) / 1000);
  } catch {
    return 0;
  }
}
